// Sprint Hub -- AgentOverlay v2 (T17)
// Multi-source citation-rich chat panel with memory and deep research.
// ASCII only -- no unicode or special characters

import { useState, useRef, useEffect, useCallback } from 'react';
import { postAsk } from '../lib/api.js';

const QUICK_PROMPTS = [
  'What needs my attention?',
  'Cash position',
  'Pipeline status',
];

// Confidence badge colors
var CONFIDENCE_STYLES = {
  high: { background: 'var(--color-status-green)', color: '#fff' },
  medium: { background: 'var(--color-status-amber, #f59e0b)', color: '#fff' },
  low: { background: 'var(--color-status-red, #ef4444)', color: '#fff' },
};

// Source type labels and route prefixes for clickable navigation
var SOURCE_ROUTES = {
  finance: '/finance',
  quickbooks: '/finance',
  pipeline: '/pipeline',
  client: '/clients',
  approval: '/approvals',
  governance: '/docs',
  team_signal: '/team',
  initiative: '/projects',
  task: '/tasks',
  email: null,
  slack: null,
  calendar: null,
};

var SOURCE_LABELS = {
  finance: 'Finance',
  quickbooks: 'QuickBooks',
  pipeline: 'Pipeline',
  client: 'Client',
  approval: 'Approval',
  governance: 'Governance',
  team_signal: 'Team',
  initiative: 'Project',
  task: 'Task',
  email: 'Email',
  slack: 'Slack',
  calendar: 'Calendar',
};

export default function AgentOverlay() {
  var _s = useState(false); var open = _s[0]; var setOpen = _s[1];
  var _i = useState(''); var input = _i[0]; var setInput = _i[1];
  var _h = useState([]); var history = _h[0]; var setHistory = _h[1];
  var _l = useState(false); var loading = _l[0]; var setLoading = _l[1];
  var _m = useState(false); var isMobile = _m[0]; var setIsMobile = _m[1];
  var _d = useState(false); var deepMode = _d[0]; var setDeepMode = _d[1];
  var _c = useState(null); var convId = _c[0]; var setConvId = _c[1];
  var _r = useState(false); var historyLoaded = _r[0]; var setHistoryLoaded = _r[1];
  var inputRef = useRef(null);
  var historyEndRef = useRef(null);

  // Detect mobile
  useEffect(function() {
    var check = function() { setIsMobile(window.innerWidth < 768); };
    check();
    window.addEventListener('resize', check);
    return function() { window.removeEventListener('resize', check); };
  }, []);

  // Scroll to bottom when history updates
  useEffect(function() {
    if (open) {
      historyEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [history, open]);

  // Focus input on open
  useEffect(function() {
    if (open) {
      setTimeout(function() { inputRef.current?.focus(); }, 100);
    }
  }, [open]);

  // Load conversation history from D1 on first open
  useEffect(function() {
    if (open && !historyLoaded) {
      loadConversationHistory();
      setHistoryLoaded(true);
    }
  }, [open, historyLoaded]);

  var loadConversationHistory = useCallback(async function() {
    try {
      var resp = await fetch('/api/ask/conversations?limit=1', {
        headers: { 'Content-Type': 'application/json' },
      });
      if (!resp.ok) return;
      var data = await resp.json();
      var convs = data?.data?.items || [];
      if (convs.length === 0) return;

      var lastConv = convs[0];
      // Only restore if updated within last 2 hours
      var twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
      if (lastConv.updated_at < twoHoursAgo) return;

      setConvId(lastConv.id);

      var detailResp = await fetch('/api/ask/conversations/' + lastConv.id, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (!detailResp.ok) return;
      var detail = await detailResp.json();
      var msgs = detail?.data?.messages || [];
      if (msgs.length === 0) return;

      var restored = msgs.map(function(m) {
        return {
          role: m.role,
          content: m.content,
          sources: m.sources || [],
          agent: m.agent,
          agentName: m.agent || null,
          model: m.model,
          confidence: m.confidence,
          followUps: m.follow_ups || [],
        };
      });
      setHistory(restored);
    } catch (e) {
      // Non-fatal: just start fresh
    }
  }, []);

  var sendMessage = useCallback(async function(query) {
    if (!query.trim() || loading) return;

    var userMsg = { role: 'user', content: query.trim() };
    setHistory(function(h) { return h.concat([userMsg]); });
    setInput('');
    setLoading(true);

    try {
      var res = await postAsk(
        query.trim(),
        history.map(function(m) { return { role: m.role, content: m.content }; }),
        deepMode
      );
      var d = res?.data || {};
      var answer = d.answer || 'No response from agent.';
      var sources = d.sources || [];
      var confidence = d.confidence || 'medium';
      var agentUsed = d.agent_used || null;
      var agentName = d.agent_name || d.agent_used || null;
      var modelUsed = d.model_used || null;
      var followUps = d.follow_ups || [];
      if (d.conversation_id) setConvId(d.conversation_id);

      setHistory(function(h) {
        return h.concat([{
          role: 'assistant',
          content: answer,
          sources: sources,
          confidence: confidence,
          agent: agentUsed,
          agentName: agentName,
          model: modelUsed,
          followUps: followUps,
        }]);
      });
    } catch (e) {
      setHistory(function(h) {
        return h.concat([{
          role: 'assistant',
          content: 'Error: ' + (e.message || 'Could not reach agent.'),
          sources: [],
          confidence: 'low',
          agent: null,
          agentName: null,
          model: null,
          followUps: [],
        }]);
      });
    } finally {
      setLoading(false);
      setTimeout(function() { inputRef.current?.focus(); }, 50);
    }
  }, [loading, history, deepMode]);

  var handleKeyDown = function(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  var handleClose = function() {
    setOpen(false);
  };

  var handleClear = function() {
    setHistory([]);
    setConvId(null);
    setHistoryLoaded(false);
  };

  var handleSourceClick = function(source) {
    if (!source) return;
    var route = SOURCE_ROUTES[source.type];
    if (route && source.id) {
      window.location.href = route + '/' + source.id;
      setOpen(false);
    }
  };

  var panelStyle = isMobile ? styles.panelMobile : styles.panelDesktop;

  // Find the last assistant message for follow-ups
  var lastAssistant = null;
  for (var idx = history.length - 1; idx >= 0; idx--) {
    if (history[idx].role === 'assistant') {
      lastAssistant = history[idx];
      break;
    }
  }
  var showFollowUps = lastAssistant && lastAssistant.followUps && lastAssistant.followUps.length > 0 && !loading;

  return (
    <>
      {/* FAB */}
      {!open && (
        <button
          style={styles.fab}
          onClick={function() { setOpen(true); }}
          aria-label="Open Ask Hub"
          title="Ask Hub"
        >
          <AgentIcon />
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <>
          {/* Backdrop on mobile */}
          {isMobile && (
            <div style={styles.backdrop} onClick={handleClose} aria-hidden="true" />
          )}

          <div style={Object.assign({}, styles.panel, panelStyle)} role="dialog" aria-label="Ask Hub agent">
            {/* Header */}
            <div style={styles.header}>
              <div style={styles.headerLeft}>
                <span style={styles.headerDot} />
                <span style={styles.headerTitle}>Ask Hub</span>
                {deepMode && <span style={styles.deepBadge}>Deep</span>}
              </div>
              <div style={styles.headerActions}>
                {/* Deep research toggle */}
                <button
                  style={Object.assign({}, styles.toggleBtn, deepMode ? styles.toggleBtnActive : {})}
                  onClick={function() { setDeepMode(function(d) { return !d; }); }}
                  aria-label={deepMode ? 'Disable deep research' : 'Enable deep research'}
                  title={deepMode ? 'Deep research ON (Opus)' : 'Enable deep research (Opus)'}
                >
                  <DeepIcon />
                </button>
                {history.length > 0 && (
                  <button
                    style={styles.clearBtn}
                    onClick={handleClear}
                    aria-label="Clear history"
                    title="Clear chat"
                  >
                    Clear
                  </button>
                )}
                <button
                  style={styles.closeBtn}
                  onClick={handleClose}
                  aria-label="Close Ask Hub"
                >
                  <CloseIcon />
                </button>
              </div>
            </div>

            {/* History */}
            <div style={styles.history} aria-live="polite" aria-label="Chat history">
              {history.length === 0 && (
                <div style={styles.emptyState}>
                  <div style={styles.emptyIcon}><AgentIconLg /></div>
                  <p style={styles.emptyTitle}>Ask Hub</p>
                  <p style={styles.emptySubtitle}>
                    Ask anything about Hub -- deals, cash, team, next actions. Sources are cited automatically.
                  </p>
                </div>
              )}
              {history.map(function(msg, i) {
                return (
                  <div
                    key={i}
                    style={Object.assign({}, styles.message, msg.role === 'user' ? styles.messageUser : styles.messageAssistant)}
                  >
                    {/* Agent identity for assistant messages */}
                    {msg.role === 'assistant' && msg.agentName && (
                      <div style={styles.agentLabel}>
                        via {formatAgentName(msg.agentName)} agent
                        {msg.confidence && (
                          <span style={Object.assign({}, styles.confidenceBadge, CONFIDENCE_STYLES[msg.confidence] || CONFIDENCE_STYLES.medium)}>
                            {msg.confidence}
                          </span>
                        )}
                      </div>
                    )}
                    <div
                      style={Object.assign({}, styles.bubble, msg.role === 'user' ? styles.bubbleUser : styles.bubbleAssistant)}
                    >
                      {msg.content}
                    </div>
                    {/* Source citations */}
                    {msg.role === 'assistant' && msg.sources && msg.sources.length > 0 && (
                      <div style={styles.sourcesRow}>
                        {deduplicateSources(msg.sources).slice(0, 6).map(function(src, si) {
                          var hasRoute = SOURCE_ROUTES[src.type] && src.id;
                          return (
                            <button
                              key={si}
                              style={Object.assign({}, styles.sourceChip, hasRoute ? styles.sourceChipClickable : {})}
                              onClick={hasRoute ? function() { handleSourceClick(src); } : undefined}
                              title={src.snippet || src.title}
                            >
                              <SourceDot type={src.type} />
                              <span style={styles.sourceLabel}>
                                {SOURCE_LABELS[src.type] || src.type}: {truncate(src.title, 28)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {loading && (
                <div style={Object.assign({}, styles.message, styles.messageAssistant)}>
                  <div style={Object.assign({}, styles.bubble, styles.bubbleAssistant)}>
                    <span style={styles.typingDots}>
                      <span />
                      <span />
                      <span />
                    </span>
                  </div>
                </div>
              )}
              <div ref={historyEndRef} />
            </div>

            {/* Quick prompts (shown when empty) */}
            {history.length === 0 && (
              <div style={styles.chips}>
                {QUICK_PROMPTS.map(function(prompt) {
                  return (
                    <button
                      key={prompt}
                      style={styles.chip}
                      onClick={function() { sendMessage(prompt); }}
                      disabled={loading}
                    >
                      {prompt}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Follow-up suggestions */}
            {showFollowUps && (
              <div style={styles.followUps}>
                <span style={styles.followUpsLabel}>Follow up:</span>
                {lastAssistant.followUps.map(function(fu, fi) {
                  return (
                    <button
                      key={fi}
                      style={styles.followUpBtn}
                      onClick={function() { sendMessage(fu); }}
                      disabled={loading}
                    >
                      {fu}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Input */}
            <div style={styles.inputRow}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={function(e) { setInput(e.target.value); }}
                onKeyDown={handleKeyDown}
                placeholder={deepMode ? 'Deep research mode (Opus)...' : 'Ask anything...'}
                style={styles.input}
                rows={1}
                disabled={loading}
                aria-label="Message input"
              />
              <button
                style={Object.assign({}, styles.sendBtn, (!input.trim() || loading) ? styles.sendBtnDisabled : {})}
                onClick={function() { sendMessage(input); }}
                disabled={!input.trim() || loading}
                aria-label="Send message"
              >
                <SendIcon />
              </button>
            </div>
          </div>
        </>
      )}

      <style>{typingAnimation}</style>
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAgentName(name) {
  if (!name) return '';
  // Convert snake_case to Title Case
  return name.split('_').map(function(w) {
    return w.charAt(0).toUpperCase() + w.slice(1);
  }).join(' ');
}

function truncate(str, maxLen) {
  if (!str) return '';
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen - 3) + '...';
}

function deduplicateSources(sources) {
  var seen = {};
  return sources.filter(function(s) {
    var key = s.type + ':' + s.id;
    if (seen[key]) return false;
    seen[key] = true;
    return true;
  });
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

var styles = {
  fab: {
    position: 'fixed',
    bottom: '1.5rem',
    right: '1.5rem',
    width: 52,
    height: 52,
    borderRadius: '9999px',
    background: 'var(--color-accent)',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(66, 99, 235, 0.40)',
    zIndex: 900,
    transition: 'transform 150ms ease, box-shadow 150ms ease',
  },
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.25)',
    zIndex: 920,
  },
  panel: {
    position: 'fixed',
    zIndex: 930,
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--color-background)',
    border: '1px solid var(--color-border)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
    overflow: 'hidden',
  },
  panelDesktop: {
    bottom: '1.5rem',
    right: '1.5rem',
    width: 400,
    maxHeight: 'calc(100vh - 4rem)',
    borderRadius: '1rem',
  },
  panelMobile: {
    inset: 0,
    borderRadius: 0,
    width: '100%',
    height: '100%',
    maxHeight: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.875rem 1rem',
    borderBottom: '1px solid var(--color-border)',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  headerDot: {
    width: 8,
    height: 8,
    borderRadius: '9999px',
    background: 'var(--color-status-green)',
    display: 'inline-block',
  },
  headerTitle: {
    fontSize: '0.9375rem',
    fontWeight: 700,
    color: 'var(--color-foreground)',
  },
  deepBadge: {
    fontSize: '0.625rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '0.125rem 0.375rem',
    borderRadius: '0.25rem',
    background: 'var(--color-accent)',
    color: '#fff',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  toggleBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: '0.375rem',
    background: 'none',
    border: '1px solid var(--color-border)',
    cursor: 'pointer',
    color: 'var(--color-muted-text)',
    transition: 'all 150ms ease',
  },
  toggleBtnActive: {
    background: 'var(--color-accent)',
    borderColor: 'var(--color-accent)',
    color: '#fff',
  },
  clearBtn: {
    fontSize: '0.75rem',
    color: 'var(--color-muted-text)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.25rem 0.5rem',
    borderRadius: '0.375rem',
  },
  closeBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: '0.375rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-muted-text)',
  },
  history: {
    flex: 1,
    overflowY: 'auto',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    minHeight: 200,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem 1rem',
    gap: '0.5rem',
    flex: 1,
    textAlign: 'center',
  },
  emptyIcon: {
    color: 'var(--color-accent)',
    marginBottom: '0.5rem',
  },
  emptyTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: 'var(--color-foreground)',
  },
  emptySubtitle: {
    fontSize: '0.8125rem',
    color: 'var(--color-muted-text)',
    lineHeight: 1.5,
    maxWidth: 280,
  },
  message: {
    display: 'flex',
    flexDirection: 'column',
  },
  messageUser: {
    alignItems: 'flex-end',
  },
  messageAssistant: {
    alignItems: 'flex-start',
  },
  agentLabel: {
    fontSize: '0.6875rem',
    color: 'var(--color-muted-text)',
    marginBottom: '0.1875rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
  },
  confidenceBadge: {
    fontSize: '0.5625rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    padding: '0.0625rem 0.3125rem',
    borderRadius: '0.1875rem',
    lineHeight: 1.4,
  },
  bubble: {
    maxWidth: '85%',
    padding: '0.625rem 0.875rem',
    borderRadius: '1rem',
    fontSize: '0.875rem',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  bubbleUser: {
    background: 'var(--color-accent)',
    color: '#fff',
    borderBottomRightRadius: '0.25rem',
  },
  bubbleAssistant: {
    background: 'var(--color-muted-bg)',
    color: 'var(--color-foreground)',
    borderBottomLeftRadius: '0.25rem',
  },
  sourcesRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.25rem',
    marginTop: '0.375rem',
    maxWidth: '85%',
  },
  sourceChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.1875rem 0.5rem',
    fontSize: '0.6875rem',
    borderRadius: '9999px',
    border: '1px solid var(--color-border)',
    background: 'var(--color-background)',
    color: 'var(--color-muted-text)',
    cursor: 'default',
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
  },
  sourceChipClickable: {
    cursor: 'pointer',
  },
  sourceLabel: {
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    padding: '0 1rem 0.75rem',
  },
  chip: {
    padding: '0.375rem 0.75rem',
    fontSize: '0.8125rem',
    fontWeight: 500,
    borderRadius: '9999px',
    border: '1px solid var(--color-border)',
    background: 'var(--color-background)',
    color: 'var(--color-muted-text)',
    cursor: 'pointer',
    transition: 'background 150ms ease, border-color 150ms ease, color 150ms ease',
    whiteSpace: 'nowrap',
  },
  followUps: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.375rem',
    padding: '0 1rem 0.5rem',
    alignItems: 'center',
  },
  followUpsLabel: {
    fontSize: '0.6875rem',
    color: 'var(--color-muted-text)',
    fontWeight: 600,
    marginRight: '0.125rem',
  },
  followUpBtn: {
    padding: '0.25rem 0.625rem',
    fontSize: '0.75rem',
    borderRadius: '9999px',
    border: '1px solid var(--color-accent)',
    background: 'var(--color-background)',
    color: 'var(--color-accent)',
    cursor: 'pointer',
    transition: 'background 150ms ease, color 150ms ease',
    whiteSpace: 'nowrap',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    borderTop: '1px solid var(--color-border)',
    flexShrink: 0,
  },
  input: {
    flex: 1,
    resize: 'none',
    border: '1px solid var(--color-border)',
    borderRadius: '0.75rem',
    padding: '0.5625rem 0.875rem',
    fontSize: '0.875rem',
    fontFamily: 'var(--font-family)',
    color: 'var(--color-foreground)',
    background: 'var(--color-background)',
    outline: 'none',
    lineHeight: 1.5,
    maxHeight: 120,
    overflowY: 'auto',
  },
  sendBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 38,
    height: 38,
    borderRadius: '9999px',
    background: 'var(--color-accent)',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'opacity 150ms ease',
  },
  sendBtnDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  typingDots: {
    display: 'inline-flex',
    gap: 4,
    alignItems: 'center',
    height: 16,
  },
};

var typingAnimation = [
  '@keyframes typingBounce {',
  '  0%, 60%, 100% { transform: translateY(0); }',
  '  30% { transform: translateY(-4px); }',
  '}',
  'span[style*="inline-flex"] > span {',
  '  width: 6px;',
  '  height: 6px;',
  '  border-radius: 50%;',
  '  background: var(--color-muted-text);',
  '  display: inline-block;',
  '  animation: typingBounce 1.2s ease infinite;',
  '}',
  'span[style*="inline-flex"] > span:nth-child(2) { animation-delay: 0.2s; }',
  'span[style*="inline-flex"] > span:nth-child(3) { animation-delay: 0.4s; }',
].join('\n');

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function AgentIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73A2 2 0 0 1 12 2z"/>
      <circle cx="9" cy="14" r="1"/>
      <circle cx="15" cy="14" r="1"/>
    </svg>
  );
}

function AgentIconLg() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73A2 2 0 0 1 12 2z"/>
      <circle cx="9" cy="14" r="1"/>
      <circle cx="15" cy="14" r="1"/>
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"/>
      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

function DeepIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      <line x1="11" y1="8" x2="11" y2="14"/>
      <line x1="8" y1="11" x2="14" y2="11"/>
    </svg>
  );
}

function SourceDot(props) {
  var colors = {
    finance: '#10b981',
    quickbooks: '#10b981',
    pipeline: '#6366f1',
    client: '#f59e0b',
    approval: '#ef4444',
    governance: '#8b5cf6',
    team_signal: '#06b6d4',
    initiative: '#3b82f6',
    task: '#64748b',
    email: '#ea580c',
    slack: '#e11d48',
    calendar: '#0891b2',
  };
  var color = colors[props.type] || '#94a3b8';
  return (
    <span style={{
      width: 6,
      height: 6,
      borderRadius: '9999px',
      background: color,
      display: 'inline-block',
      flexShrink: 0,
    }} />
  );
}
